import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  Account,
  PermissionValues,
  Role,
  canModifyUserRole,
  getAvailableRoles,
  useCurrentAccount,
} from '@gripp/shared-logic';
import { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { Menu } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { AccountTypeSelector } from './accountTypeSelector';
import { onPressProps } from './getAccountColumn';
import { textStyles } from '../../styles';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

type AccountChipMenuProps = {
  workspaceId: string | undefined;
  account: Account;
  onPress: (props: onPressProps) => Promise<void>;
  roleList: Role[];
  showGlobalAdminRole: boolean;
  currentUserRole?: string;
};

export const AccountTypeMenu = ({
  workspaceId,
  account,
  onPress,
  roleList,
  showGlobalAdminRole,
  currentUserRole,
}: AccountChipMenuProps) => {
  const [visible, setVisible] = useState(false);
  const { account: currentUser } = useCurrentAccount();

  const openMenu = () => setVisible(true);
  const closeMenu = () => setVisible(false);

  const role = account?.workspaceRoles?.find(
    (wr) => wr.workspace === workspaceId
  );

  const roleName = roleList?.find((r) => r.id === role?.role)?.name;

  if (!role || !roleName) {
    return null;
  }

  // Check if current user has permission to change roles and hierarchy allows it
  const hasPermission =
    currentUser?.hasPermission(PermissionValues.account.changeRole) ?? false;

  const canModify = currentUserRole
    ? hasPermission && canModifyUserRole(currentUserRole, roleName)
    : false;

  // If user cannot modify this account's role, show read-only view
  if (!canModify) {
    return (
      <View style={styles.readOnlyContainer}>
        <Text style={styles.readOnlyText}>{roleName}</Text>
      </View>
    );
  }

  // Get available roles using the consolidated function
  const availableRoles = getAvailableRoles(
    roleList,
    currentUserRole,
    showGlobalAdminRole
  );

  return (
    <Menu
      visible={visible}
      onDismiss={closeMenu}
      contentStyle={styles.menuContent}
      anchor={
        <Pressable onPress={openMenu}>
          <View style={styles.anchor}>
            <Text style={textStyles.default}>{roleName}</Text>
            <MaterialCommunityIcons
              name={visible ? 'chevron-up' : 'chevron-down'}
              size={24}
              color={Colors.role.textPrimary}
            />
          </View>
        </Pressable>
      }
    >
      <AccountTypeSelector
        account={account}
        roleId={role.role}
        onSelect={onPress}
        roleList={availableRoles}
        closeMenu={closeMenu}
        currentUserRole={currentUserRole}
      />
    </Menu>
  );
};

const styles = themedStyleSheet((role) => ({
  menuContent: {
    backgroundColor: role.surface,
    flexDirection: 'column',
    flex: 1,
    top: 30,
  },
  anchor: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  readOnlyContainer: {
    padding: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  readOnlyText: {
    fontSize: 14,
    fontWeight: 500,
    color: role.textPrimary,
  },
}));
